class ThemeDrawer {
  constructor(element) {
    this.element = element;
    this.opener = null;
    // The drawer contents are replaced after every cart update. Delegate this
    // listener from the persistent outer element so the new close buttons work.
    element.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
    element.addEventListener('keydown', (event) => this.handleKeydown(event));
  }
  getFocusable() {
    return [...this.element.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((item) => item.offsetParent !== null);
  }
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = this.getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
  open(opener = document.activeElement) {
    this.opener = opener;
    this.element.classList.add('is-open');
    this.element.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    this.getFocusable()[0]?.focus();
  }
  close() {
    if (!this.element.classList.contains('is-open')) return;
    this.element.classList.remove('is-open');
    this.element.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    this.opener?.focus();
    this.opener = null;
  }
}

const cartDrawerElement = document.querySelector('#CartDrawer');
const cartDrawer = cartDrawerElement ? new ThemeDrawer(cartDrawerElement) : null;

function bindGlobalUi() {
  document.querySelectorAll('[data-cart-open]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', (event) => {
      if (!cartDrawer) return;
      event.preventDefault();
      cartDrawer.open(button);
    });
  });

  const mobileNav = document.querySelector('#MobileNav');
  document.querySelectorAll('[data-mobile-nav-open]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => {
      mobileNav?.classList.add('is-open');
      mobileNav?.setAttribute('aria-hidden', 'false');
      button.setAttribute('aria-expanded', 'true');
      document.body.classList.add('no-scroll');
      mobileNav?.querySelector('button, a, summary')?.focus();
    });
  });
  document.querySelectorAll('[data-mobile-nav-close]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => {
      mobileNav?.classList.remove('is-open');
      mobileNav?.setAttribute('aria-hidden', 'true');
      document.querySelector('[data-mobile-nav-open]')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('no-scroll');
      document.querySelector('[data-mobile-nav-open]')?.focus();
    });
  });

  document.querySelectorAll('[data-quick-view]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => {
      const dialog = document.getElementById(button.dataset.quickView);
      if (dialog) {
        dialog.dataset.openerId = button.id;
        dialog.showModal();
      }
    });
  });
  document.querySelectorAll('[data-dialog-close]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => button.closest('dialog')?.close());
  });

  document.querySelectorAll('dialog.quick-view').forEach((dialog) => {
    if (dialog.dataset.bound) return;
    dialog.dataset.bound = 'true';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      if (dialog.dataset.openerId) document.getElementById(dialog.dataset.openerId)?.focus();
    });
  });

  document.querySelectorAll('[data-filter-open]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => document.querySelector('#Facets')?.classList.add('is-open'));
  });
  document.querySelectorAll('[data-filter-close]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => document.querySelector('#Facets')?.classList.remove('is-open'));
  });

  document.querySelectorAll('[data-quantity]').forEach((wrapper) => {
    if (wrapper.dataset.bound) return;
    wrapper.dataset.bound = 'true';
    const input = wrapper.querySelector('input');
    wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      input.value = Math.max(Number(input.min || 1), Number(input.value) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      input.value = Number(input.value) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

async function refreshCart(openDrawer = false) {
  if (!cartDrawerElement) return;
  const response = await fetch(`${window.themeRoutes.cart}?sections=cart-drawer`);
  if (!response.ok) throw new Error(window.themeStrings.cartError);
  const sections = await response.json();
  if (!sections['cart-drawer']) throw new Error(window.themeStrings.cartError);
  const parser = new DOMParser();
  const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
  const replacement = doc.querySelector('#CartDrawer');
  if (replacement) {
    cartDrawerElement.innerHTML = replacement.innerHTML;
    bindCartActions();
    bindGlobalUi();
    document.dispatchEvent(new CustomEvent('theme:content-loaded'));
  }
  const cartResponse = await fetch(`${window.themeRoutes.cart}.js`);
  if (!cartResponse.ok) throw new Error(window.themeStrings.cartError);
  const cart = await cartResponse.json();
  document.querySelectorAll('[data-cart-count]').forEach((count) => count.textContent = cart.item_count);
  if (openDrawer) cartDrawer.open();
}

async function addToCart(form) {
  const button = form.querySelector('[type="submit"]');
  const original = button?.textContent;
  if (button) { button.disabled = true; button.textContent = '…'; }
  try {
    const response = await fetch(window.themeRoutes.cartAdd, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || window.themeStrings.cartError);
    }
    await refreshCart(true);
  } catch (error) {
    alert(error.message);
  } finally {
    if (button) { button.disabled = false; button.textContent = original; }
  }
}

function bindProductForms() {
  document.querySelectorAll('[data-ajax-product-form]').forEach((form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = 'true';
    form.addEventListener('submit', (event) => {
      if (!cartDrawer || !document.documentElement.classList.contains('js')) return;
      event.preventDefault();
      addToCart(form);
    });
  });
}

function bindCartActions() {
  document.querySelectorAll('[data-cart-remove]').forEach((button) => {
    if (button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', async () => {
      button.disabled = true;
      try {
        const response = await fetch(window.themeRoutes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: button.dataset.cartRemove, quantity: 0 })
        });
        if (!response.ok) throw new Error(window.themeStrings.cartError);
        await refreshCart(false);
      } catch (error) {
        button.disabled = false;
        alert(error.message || window.themeStrings.cartError);
      }
    });
  });
}

function initPredictiveSearch() {
  document.querySelectorAll('[data-predictive-search]').forEach((container) => {
    const input = container.querySelector('input[type="search"]');
    const results = container.querySelector('[data-predictive-results]');
    if (!input || !results || input.dataset.bound) return;
    input.dataset.bound = 'true';
    let controller;
    input.addEventListener('input', async () => {
      const query = input.value.trim();
      if (query.length < 2) { results.innerHTML = ''; return; }
      controller?.abort();
      controller = new AbortController();
      try {
        const url = `${window.themeRoutes.predictiveSearch}?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=6`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Predictive search failed');
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        results.innerHTML = doc.querySelector('[data-predictive-content]')?.innerHTML || '';
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    });
    document.addEventListener('click', (event) => {
      if (!container.contains(event.target)) results.innerHTML = '';
    });
  });
}

function initProductGallery() {
  document.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
    if (thumb.dataset.bound) return;
    thumb.dataset.bound = 'true';
    thumb.addEventListener('click', () => {
      const gallery = thumb.closest('[data-product-gallery]');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.setAttribute('aria-current', 'false'));
      gallery.querySelectorAll('[data-gallery-media]').forEach((item) => item.classList.remove('is-active'));
      thumb.setAttribute('aria-current', 'true');
      gallery.querySelector(`[data-gallery-media="${thumb.dataset.galleryThumb}"]`)?.classList.add('is-active');
    });
  });
}

function initVariantSelectors() {
  document.querySelectorAll('[data-product-root]').forEach((root) => {
    const json = root.querySelector('[data-product-json]');
    const select = root.querySelector('[data-variant-select]');
    if (!json || !select) return;
    if (select.dataset.bound) return;
    select.dataset.bound = 'true';
    let variants;
    try { variants = JSON.parse(json.textContent); } catch (error) { return; }
    select.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === select.value);
      if (!variant) return;
      root.querySelectorAll('[name="id"]').forEach((input) => input.value = variant.id);
      const price = root.querySelector('[data-product-price]');
      if (price) price.textContent = variant.price_formatted;
      const button = root.querySelector('[data-add-button]');
      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? window.themeStrings.addToCart : window.themeStrings.soldOut;
      }
      if (history.replaceState && variant.url) history.replaceState({}, '', variant.url);
    });
  });
}

function initFacets() {
  document.querySelectorAll('[data-facet-form]').forEach((form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = 'true';
    form.addEventListener('change', () => form.submit());
  });
  document.querySelectorAll('[data-sort-by]').forEach((select) => {
    if (select.dataset.bound) return;
    select.dataset.bound = 'true';
    select.addEventListener('change', (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      window.location.assign(url.toString());
    });
  });
}

function initLocalization() {
  document.querySelectorAll('[data-localization-select]').forEach((select) => {
    if (select.dataset.bound) return;
    select.dataset.bound = 'true';
    select.addEventListener('change', () => select.closest('form').submit());
  });
}

document.addEventListener('keydown', (event) => {
  const mobileNav = document.querySelector('#MobileNav');
  if (!mobileNav?.classList.contains('is-open')) return;
  if (event.key === 'Escape') {
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    const opener = document.querySelector('[data-mobile-nav-open]');
    opener?.setAttribute('aria-expanded', 'false');
    opener?.focus();
    return;
  }
  if (event.key === 'Tab') {
    const focusable = [...mobileNav.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((item) => item.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
});

document.addEventListener('theme:content-loaded', () => {
  bindGlobalUi();
  bindProductForms();
});

document.documentElement.classList.add('js');
document.addEventListener('DOMContentLoaded', () => {
  bindGlobalUi();
  bindProductForms();
  bindCartActions();
  initPredictiveSearch();
  initProductGallery();
  initVariantSelectors();
  initFacets();
  initLocalization();
});
